import { Fragment, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AdminPage, Pill, statusTone } from "@/pages/admin/AdminLayout";

const STATUSES = ["IDEA", "RESEARCH", "DRAFT", "REVIEW", "READY", "PUBLISHED", "PAUSED", "ARCHIVED"];
const ROLES = ["PRIMARY", "SUPPORTING", "TOOL", "FAQ", "FUTURE"];

export default function ArticlesAdmin() {
  const [rows, setRows] = useState([]);
  const [barriers, setBarriers] = useState([]);
  const [openSlug, setOpenSlug] = useState(null);
  const [draft, setDraft] = useState({});

  const load = () => api.get("/admin/articles").then((r) => setRows(r.data));

  useEffect(() => {
    load();
    api.get("/settings").then((r) => setBarriers(r.data.purchase_barriers || []));
  }, []);

  const patch = async (slug, body) => {
    try {
      const { data } = await api.patch(`/admin/articles/${slug}`, body);
      setRows((rs) => rs.map((r) => (r.slug === slug ? { ...r, ...data } : r)));
      toast.success("Saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save changes");
    }
  };

  const openEditor = (r) => {
    setOpenSlug(openSlug === r.slug ? null : r.slug);
    setDraft(r);
  };

  const publishedCount = rows.filter((r) => r.status === "PUBLISHED").length;

  return (
    <AdminPage
      title="Articles & questions"
      lede={`${rows.length} records — ${publishedCount} published. Every record carries its question role, buyer stage, purchase barrier, barrier resolution, commercial next step and monetization path. Status changes take effect on the public site immediately.`}
    >
      <div className="bg-white border border-line overflow-x-auto" data-testid="admin-articles-table">
        <table className="w-full min-w-[1000px] text-left">
          <thead>
            <tr className="bg-bone border-b border-line">
              {["Title", "Hub", "Role", "Barrier", "Tool", "Offer category", "Status", "Fact-check", ""].map((h) => (
                <th key={h} className="px-4 py-3 text-[9.5px] tracking-[0.16em] uppercase text-[#6E6A63] font-medium">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <Fragment key={r.slug}>
                <tr className="border-b border-line/70" data-testid={`admin-article-row-${r.slug}`}>
                  <td className="px-4 py-3.5 max-w-[300px]">
                    <div className="text-[14px] text-charcoal leading-snug">{r.title}</div>
                    <div className="text-[11px] font-light text-[#736F68] mt-1">
                      {r.depth} · /{r.slug}
                    </div>
                  </td>
                  <td className="px-4 py-3.5 text-[12.5px] font-light text-[#5A5A5A]">{r.hub}</td>
                  <td className="px-4 py-3.5">
                    <select
                      value={r.question_role}
                      onChange={(e) => patch(r.slug, { question_role: e.target.value })}
                      data-testid={`article-role-${r.slug}`}
                      className="text-[12px] border border-line px-2 py-1.5 bg-white"
                    >
                      {ROLES.map((s) => (
                        <option key={s}>{s}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-3.5 text-[12.5px] font-light text-[#5A5A5A]">{r.purchase_barrier}</td>
                  <td className="px-4 py-3.5 text-[12px] font-light text-[#5A5A5A]">{r.related_tool || "—"}</td>
                  <td className="px-4 py-3.5 text-[12px] font-light text-[#5A5A5A]">
                    {r.primary_offer_category || "—"}
                  </td>
                  <td className="px-4 py-3.5">
                    <select
                      value={r.status}
                      onChange={(e) => patch(r.slug, { status: e.target.value })}
                      data-testid={`article-status-${r.slug}`}
                      className="text-[12px] border border-line px-2 py-1.5 bg-white"
                    >
                      {STATUSES.map((s) => (
                        <option key={s}>{s}</option>
                      ))}
                    </select>
                    <div className="mt-1.5">
                      <Pill tone={statusTone(r.status)}>{r.status}</Pill>
                    </div>
                  </td>
                  <td className="px-4 py-3.5 text-[12px] font-light">
                    {r.last_fact_check ? (
                      <span className="text-[#166534]">{r.last_fact_check.slice(0, 10)}</span>
                    ) : (
                      <span className="text-[#B4741F]">pending</span>
                    )}
                  </td>
                  <td className="px-4 py-3.5">
                    <button
                      onClick={() => openEditor(r)}
                      data-testid={`article-edit-${r.slug}`}
                      className="text-[11.5px] tracking-[0.12em] uppercase text-sage hover:text-charcoal transition-colors"
                    >
                      {openSlug === r.slug ? "Close" : "Edit"}
                    </button>
                  </td>
                </tr>
                {openSlug === r.slug && (
                  <tr className="bg-bone border-b border-line">
                    <td colSpan={9} className="px-4 py-6">
                      <div className="grid md:grid-cols-2 gap-6 max-w-4xl" data-testid={`article-editor-${r.slug}`}>
                        {[
                          ["target_question", "Target question"],
                          ["search_intent", "Search intent"],
                          ["buyer_stage", "Buyer stage"],
                          ["product_family", "Product family"],
                          ["barrier_resolved", "Barrier resolved"],
                          ["commercial_next_step", "Commercial next step"],
                          ["reason_to_exist", "Reason the content deserves to exist"],
                          ["meta_description", "Meta description"],
                        ].map(([k, label]) => (
                          <label key={k} className="block">
                            <span className="text-[10px] tracking-[0.16em] uppercase text-[#6E6A63]">{label}</span>
                            <textarea
                              rows={2}
                              value={draft[k] || ""}
                              onChange={(e) => setDraft({ ...draft, [k]: e.target.value })}
                              className="w-full border border-line px-3 py-2.5 mt-1.5 text-[13.5px] bg-white focus:outline-none focus:border-sage"
                            />
                          </label>
                        ))}
                        <label className="block">
                          <span className="text-[10px] tracking-[0.16em] uppercase text-[#6E6A63]">
                            Purchase barrier
                          </span>
                          <select
                            value={draft.purchase_barrier || ""}
                            onChange={(e) => setDraft({ ...draft, purchase_barrier: e.target.value })}
                            className="w-full border border-line px-3 py-2.5 mt-1.5 text-[13.5px] bg-white"
                          >
                            {barriers.map((b) => (
                              <option key={b}>{b}</option>
                            ))}
                          </select>
                        </label>
                        <label className="block">
                          <span className="text-[10px] tracking-[0.16em] uppercase text-[#6E6A63]">
                            Last fact-check date
                          </span>
                          <input
                            type="date"
                            value={(draft.last_fact_check || "").slice(0, 10)}
                            onChange={(e) => setDraft({ ...draft, last_fact_check: e.target.value })}
                            className="w-full border border-line px-3 py-2.5 mt-1.5 text-[13.5px] bg-white"
                          />
                        </label>
                      </div>
                      <button
                        onClick={() => {
                          const body = {};
                          [
                            "target_question", "search_intent", "buyer_stage", "product_family",
                            "barrier_resolved", "commercial_next_step", "reason_to_exist",
                            "meta_description", "purchase_barrier", "last_fact_check",
                          ].forEach((k) => {
                            if (draft[k]) body[k] = draft[k];
                          });
                          patch(r.slug, body);
                        }}
                        data-testid={`article-save-${r.slug}`}
                        className="mt-6 px-7 py-3 bg-charcoal text-white text-[11.5px] tracking-[0.14em] uppercase hover:bg-sage transition-colors"
                      >
                        Save metadata
                      </button>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </AdminPage>
  );
}
